import React, { useState, useEffect } from 'react';
import { Button, Card, Row, Col, Badge, Modal, Form, Alert, Spinner, Table, InputGroup, FormControl, ListGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faTrash, 
  faEye, 
  faUser, 
  faCalendarAlt, 
  faTimes, 
  faCheck, 
  faCog 
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { Client, Event, EventColor } from '../interfaces';
import axios from 'axios';
import ClientForm from './ClientForm';
import MessageModal from './MessageModal';
import ConfirmationModal from './ConfirmationModal';
import { API_BASE_URL, buildApiUrl } from '../config/api';

const ClientList = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState<string>('');
  
  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const [messageModalTitle, setMessageModalTitle] = useState<string>('');
  const [messageModalContent, setMessageModalContent] = useState<string>('');
  const [messageModalVariant, setMessageModalVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState<string>('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmModalTitle, setConfirmModalTitle] = useState<string>('');

  const [showForm, setShowForm] = useState<boolean>(false);
  const [clientToEdit, setClientToEdit] = useState<Client | null>(null);
  
  // תיקון 11: מצב למודל של אירועי לקוח
  const [showClientEventsModal, setShowClientEventsModal] = useState<boolean>(false);
  const [selectedClientEvents, setSelectedClientEvents] = useState<any[]>([]);
  const [selectedClientName, setSelectedClientName] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [loadingClientEvents, setLoadingClientEvents] = useState<boolean>(false);
  
  const fetchClients = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {}; // Changed QueryParams to any as it's not imported
      if (searchQuery) params.search = searchQuery;

      const response = await axios.get(buildApiUrl('settings/clients/'), { params });
      setClients(response.data);
    } catch (err) {
      console.error("שגיאה בטעינת לקוחות:", err);
      setError("לא ניתן לטעון את רשימת הלקוחות.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchClients();
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);
  
  const showMessage = (title: string, message: string, variant: 'success' | 'danger' | 'info' | 'warning' = 'info') => {
      setMessageModalTitle(title);
      setMessageModalContent(message);
      setMessageModalVariant(variant);
      setShowMessageModal(true);
  };
  
  const handleAddClick = () => {
    setClientToEdit(null);
    setShowForm(true);
  };
  
  const handleEditClick = (client: Client) => {
    setClientToEdit(client);
    setShowForm(true);
  };
  
  const handleDeleteClick = (id: number) => {
    setConfirmModalTitle('אישור מחיקה');
    setConfirmModalMessage('האם אתה בטוח שברצונך למחוק לקוח זה?');
    setConfirmAction(() => async () => {
      try {
        const client = clients.find(c => c.id === id);
        await axios.delete(buildApiUrl(`settings/clients/${id}/`));
        showMessage('הצלחה', `הלקוח ${client ? `"${client.client_name}" ` : ''}נמחק בהצלחה!`, 'success');
        fetchClients();
      } catch (err) {
        showMessage('שגיאה', 'שגיאה במחיקת הלקוח.', 'danger');
      }
    });
    setShowConfirmModal(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    fetchClients();
    showMessage('הצלחה', clientToEdit ? `פרטי הלקוח "${clientToEdit.client_name}" עודכנו בהצלחה!` : 'לקוח חדש נוצר בהצלחה!', 'success');
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setClientToEdit(null);
  };

  // תיקון 11: פונקציה לטעינת אירועי לקוח
  const handleShowClientEvents = async (client: Client) => {
    setSelectedClientName(client.client_name);
    setSelectedClient(client);
    setLoadingClientEvents(true);
    setShowClientEventsModal(true);
    
    try {
      const response = await axios.get(buildApiUrl(`events/events/?client=${client.id}`));
      setSelectedClientEvents(response.data);
    } catch (err) {
      console.error("שגיאה בטעינת אירועי לקוח:", err);
      showMessage('שגיאה', 'שגיאה בטעינת אירועי הלקוח.', 'danger');
    } finally {
      setLoadingClientEvents(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 app-sticky-page-toolbar">
        <h2>ניהול לקוחות</h2>
                  <Button variant="success" onClick={handleAddClick}>
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    הוסף לקוח חדש
                  </Button>
      </div>

      <Form className="mb-4 p-3 border rounded bg-light">
        <Row>
          <Col md={12}>
            <Form.Group controlId="searchQuery">
              <Form.Label>חיפוש</Form.Label>
              <Form.Control type="text" placeholder="חפש לפי שם, איש קשר, טלפון..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </Form.Group>
          </Col>
        </Row>
      </Form>

      {loading && <Spinner animation="border" />}
      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && !error && (
        !clients || clients.length === 0 ? (
          <p>אין לקוחות במערכת.</p>
        ) : (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>שם לקוח</th>
                <th>איש קשר</th>
                <th>טלפון</th>
                <th>אימייל</th>
                <th>ציוד מומלץ</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {clients.map(client => (
                <tr
                  key={client.id}
                  onClick={() => handleShowClientEvents(client)}
                  style={{ cursor: 'pointer' }}
                  title="לחץ לצפייה בפרטי הלקוח ובכל האירועים שלו"
                >
                  <td>{client.client_name}</td>
                  <td>{client.contact_person || 'N/A'}</td>
                  <td>{client.phone || 'N/A'}</td>
                  <td>{client.email || 'N/A'}</td>
                  <td>
                    {client.recommended_equipment_details && client.recommended_equipment_details.length > 0 ? (
                      <ListGroup variant="flush">
                        {client.recommended_equipment_details.map(item => (
                          <ListGroup.Item key={item.id || `${client.id}-${item.equipment}`}>
                            {item.quantity}x {item.equipment_name}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : 'אין'}
                  </td>
                  <td>
                    <Button variant="info" size="sm" className="me-2" onClick={(e) => { e.stopPropagation(); handleShowClientEvents(client); }}>
                      <FontAwesomeIcon icon={faEye} className="me-1" />
                      אירועי לקוח
                    </Button>
                    <Button variant="warning" size="sm" className="me-2" onClick={(e) => { e.stopPropagation(); handleEditClick(client); }}>
                      <FontAwesomeIcon icon={faEdit} className="me-1" />
                      ערוך
                    </Button>
                    <Button variant="danger" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteClick(client.id!)}} disabled={user?.role?.name === 'WAREHOUSE'} title={user?.role?.name === 'WAREHOUSE' ? 'למחסנאי אין הרשאה למחיקה' : ''}>
                      <FontAwesomeIcon icon={faTrash} className="me-1" />
                      מחק
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )
      )}

      <Modal show={showForm} onHide={handleFormCancel} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{clientToEdit ? 'עריכת לקוח' : 'הוספת לקוח חדש'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ClientForm
            clientToEdit={clientToEdit}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        </Modal.Body>
      </Modal>

      {/* תיקון 11: מודל אירועי לקוח */}
      <Modal
        show={showClientEventsModal}
        onHide={() => {
          setShowClientEventsModal(false);
          setSelectedClient(null);
          setSelectedClientEvents([]);
          setSelectedClientName('');
        }}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>אירועי לקוח: {selectedClientName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedClient && (
            <Card className="mb-3">
              <Card.Body>
                <Row className="g-3">
                  <Col md={6}>
                    <div><strong>שם לקוח:</strong> {selectedClient.client_name}</div>
                    <div><strong>איש קשר:</strong> {selectedClient.contact_person || 'N/A'}</div>
                    <div><strong>טלפון:</strong> {selectedClient.phone || 'N/A'}</div>
                    <div><strong>אימייל:</strong> {selectedClient.email || 'N/A'}</div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-2"><strong>ציוד מומלץ:</strong></div>
                    {selectedClient.recommended_equipment_details && selectedClient.recommended_equipment_details.length > 0 ? (
                      <ListGroup variant="flush">
                        {selectedClient.recommended_equipment_details.map(item => (
                          <ListGroup.Item key={item.id || `${selectedClient.id}-${item.equipment}`}>
                            {item.quantity}x {item.equipment_name}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (
                      <div>אין</div>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {loadingClientEvents ? (
            <div className="text-center">
              <Spinner animation="border" />
              <p className="mt-2">טוען אירועים...</p>
            </div>
          ) : selectedClientEvents.length === 0 ? (
            <Alert variant="info">אין אירועים ללקוח זה.</Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>שם אירוע</th>
                  <th>תאריך התחלה</th>
                  <th>תאריך סיום</th>
                  <th>מיקום</th>
                  <th>סטטוס</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {selectedClientEvents.map((event: any) => (
                  <tr key={event.id}>
                    <td>{event.event_name}</td>
                    <td>{new Date(event.start_date).toLocaleString('he-IL')}</td>
                    <td>{new Date(event.end_date).toLocaleString('he-IL')}</td>
                    <td>{event.location_name || event.free_text_location || 'N/A'}</td>
                    <td>
                      <span className={`badge bg-${event.status === 'completed' ? 'success' : event.status === 'cancelled' ? 'danger' : 'primary'}`}>
                        {event.status === 'planned' ? 'מתוכנן' : 
                         event.status === 'preparation' ? 'בהכנה' : 
                         event.status === 'active' ? 'פעיל' : 
                         event.status === 'completed' ? 'הסתיים' : 
                         event.status === 'cancelled' ? 'בוטל' : event.status}
                      </span>
                    </td>
                    <td>
                                             <Button variant="info" size="sm" className="me-2">
                         <FontAwesomeIcon icon={faEye} className="me-1" />
                         פרטים
                       </Button>
                       <Button variant="primary" size="sm" className="me-2">
                         <FontAwesomeIcon icon={faCog} className="me-1" />
                         ניהול ציוד
                       </Button>
                       <Button variant="warning" size="sm">
                         <FontAwesomeIcon icon={faCog} className="me-1" />
                         הכנה
                       </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
      </Modal>

      <MessageModal show={showMessageModal} onHide={() => setShowMessageModal(false)} title={messageModalTitle} message={messageModalContent} variant={messageModalVariant} />
      <ConfirmationModal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} title={confirmModalTitle} message={confirmModalMessage} onConfirm={confirmAction || (() => {})} />
    </div>
  );
};

export default ClientList;
